import { Box, Flex, Select, Spinner, Text } from '@chakra-ui/react'
import React from 'react'
import DailyMetricBarChart from './DailyMetricBarChart'

export type MetricGranularity = 'daily' | 'weekly'

export interface GranularitySeries {
  isLoading: boolean
  isError: boolean
  points: { day: string; value: number }[]
}

interface GranularitySwitchChartProps {
  title: string
  valueLabel: string
  daily: GranularitySeries
  weekly: GranularitySeries
  dailySubtitle: (bucketCount: number) => string
  weeklySubtitle: (bucketCount: number) => string
}

// A bar chart whose daily and weekly series come from separate queries (both
// fetched up front by the parent), with a dropdown to switch between them.
const GranularitySwitchChart: React.FC<GranularitySwitchChartProps> = ({
  title,
  valueLabel,
  daily,
  weekly,
  dailySubtitle,
  weeklySubtitle,
}) => {
  const [granularity, setGranularity] = React.useState<MetricGranularity>('weekly')
  const series = granularity === 'weekly' ? weekly : daily

  const granularitySelect = (
    <Select
      size='sm'
      maxW='120px'
      value={granularity}
      onChange={event => setGranularity(event.target.value as MetricGranularity)}
    >
      <option value='daily'>Daily</option>
      <option value='weekly'>Weekly</option>
    </Select>
  )

  if (series.isLoading) {
    return (
      <Flex
        bg='app.bg'
        borderWidth='1px'
        borderColor='app.border'
        borderRadius='lg'
        height='340px'
        align='center'
        justify='center'
      >
        <Spinner />
      </Flex>
    )
  }
  if (series.isError) {
    return (
      <Box bg='app.bg' borderWidth='1px' borderColor='app.border' borderRadius='lg' p={5}>
        <Text color='red.500'>Could not load this widget.</Text>
      </Box>
    )
  }

  const subtitle =
    granularity === 'weekly'
      ? weeklySubtitle(series.points.length)
      : dailySubtitle(series.points.length)

  return (
    <DailyMetricBarChart
      data={series.points}
      title={title}
      subtitle={subtitle}
      valueLabel={valueLabel}
      headerAction={granularitySelect}
    />
  )
}

export default GranularitySwitchChart
